import { AfterViewInit, Component, Inject, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { ErrorScreenLayoutComponent } from '../shared/error-screen-layout.component';
import {
  ErrorScreenCopy,
  ErrorScreenCopyByLocale,
} from '../shared/error-screen-copy';
import { resolveErrorScreenLocale } from '../shared/error-screen-locale';
import { ErrorScreenTelemetryService } from 'src/app/core/services/error-screen-telemetry.service';
import { ErrorScreenRecoveryService } from 'src/app/core/services/error-screen-recovery.service';
import { ErrorScreenRecoveryAction } from 'src/app/core/domain/error-screen';
import { createUnicornIncidentCode } from 'src/app/core/domain/unicorn';

/**
 * Spec 051 (error-screen-parity).
 * Phase 3: FR-008 translated (en/es + English fallback), FR-009 no status-code
 * heading, FR-010 independent of the failing application.
 * Phase 2: FR-005 — a 404 is a stale/wrong URL, so leaving it for the caregiver's
 * start IS the state-changing action (`/` cannot itself produce a 404, so FR-006
 * is satisfied and "go to my start" is a legitimate sole action). FR-012 records
 * whether it worked.
 */
export const PAGE_NOT_FOUND_COPY = {
  en: {
    logoAlt: 'rettX',
    heading: 'We can’t find that page',
    body:
      'The page you were looking for isn’t here — it may have moved or the link may be out of date. You can head back to the home screen and continue from there.',
    homeLink: 'Go to the home screen',
  },
  es: {
    logoAlt: 'rettX',
    heading: 'No encontramos esa página',
    body:
      'La página que buscabas no está aquí; es posible que se haya movido o que el enlace esté desactualizado. Puedes volver a la pantalla de inicio y continuar desde allí.',
    homeLink: 'Ir a la pantalla de inicio',
  },
} satisfies ErrorScreenCopyByLocale;

@Component({
  selector: 'app-page-not-found',
  standalone: true,
  imports: [ErrorScreenLayoutComponent],
  templateUrl: './page-not-found.component.html',
  styleUrls: ['./page-not-found.component.scss'],
})
export class PageNotFoundComponent implements AfterViewInit {
  private telemetry = inject(ErrorScreenTelemetryService);
  private recovery = inject(ErrorScreenRecoveryService);
  private recorded = false;
  readonly copy: ErrorScreenCopy;
  readonly incidentCode = createUnicornIncidentCode();

  constructor(@Inject(DOCUMENT) document: Document) {
    this.copy = PAGE_NOT_FOUND_COPY[resolveErrorScreenLocale(document)];
  }

  // Spec 051 FR-001/FR-002: record this screen's appearance after it renders.
  ngAfterViewInit(): void {
    if (this.recorded) {
      return;
    }
    this.recorded = true;
    this.telemetry.recordScreenShown('page-not-found');
  }

  // Spec 051 Phase 2 FR-005: leave the dead URL for the caregiver's start —
  // the state-changing action for a 404. FR-012 records whether it worked.
  recover(action: ErrorScreenRecoveryAction): void {
    this.recovery.recover({
      screen: 'page-not-found',
      action,
      incidentCode: this.incidentCode,
      arrivalReason: '',
      destination: '/',
    });
  }
}
